import { useEffect, useRef, useState } from "react";
import styles from "./hero-full-width-video.module.less";
import { isInDebugMode } from "../../../helpers/debug-helper";
import animations from "../../../animation/animation.module.less";
import { useIntersectionObserver } from "usehooks-ts";
import { PrimaryIconButton, SecondaryIconButton } from "../button/button";
import { useTranslation } from "react-i18next";

type THeroFullWidthVideoProps = {
  videoUrlDesktop: string;
  videoUrlMobile?: string;
  enableImageOverlay?: boolean;
  enablePauseButton?: boolean;
  blockIndex?: number;
};

const videoSettings = {
  autoPlay: true,
  loop: true,
  muted: true,
  playsInline: true,
  controls: false,
};

const HeroFullWidthVideo = ({
  videoUrlDesktop,
  videoUrlMobile,
  enableImageOverlay,
  enablePauseButton = false,
  blockIndex,
}: THeroFullWidthVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasIntersected, setHasIntersected] = useState(false);
  const [userPaused, setUserPaused] = useState(false);

  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0,
  });

  useEffect(() => {
    if (isIntersecting) {
      setHasIntersected(true);
    }

    if (isIntersecting && videoRef.current && !userPaused) {
      videoRef.current.play();

      if (isInDebugMode()) {
        console.log("Playing video");
      }
    }

    if (!isIntersecting && videoRef.current) {
      videoRef.current.pause();

      if (isInDebugMode()) {
        console.log("Pausing video");
      }
    }
  }, [isIntersecting]);

  useEffect(() => {
    if (userPaused) {
      videoRef.current?.pause();
    } else {
      videoRef.current?.play();
    }
  }, [userPaused]);

  const animationClasses = [
    blockIndex === 0 ? animations.fadeIn400 : hasIntersected ? animations.fadeIn400 : "",
    animations.opacity0,
  ].join(" ");

  const { t } = useTranslation();

  return (
    <div className={animationClasses}>
      {enableImageOverlay && <div className={styles.imageOverlay} />}
      <div
        onClick={() => setUserPaused(!userPaused)}
        ref={ref}
        className={[styles.fullWidthVideoWrapper, enableImageOverlay ? styles.enableImageOverlay : ""].join(" ")}
      >
        {enablePauseButton && (
          <SecondaryIconButton
            onClick={() => setUserPaused(!userPaused)}
            className={styles.pauseButton}
            icon={userPaused ? "ui-icon-play-filled" : "ui-icon-pause-filled"}
            ariaLabel={userPaused ? t("PLAY_VIDEO") : t("PAUSE_VIDEO")}
          />
        )}
        <video ref={videoRef} {...videoSettings} className={styles.fullWidthVideo}>
          <source src={videoUrlMobile} type="video/mp4" media="(max-width: 767px)" />
          <source src={videoUrlDesktop} type="video/mp4" media="(min-width: 768px)" />
        </video>
      </div>
    </div>
  );
};

export default HeroFullWidthVideo;
